import i18n from '../../../../i18n';
import { BackgroundLabelQuery } from '../../../../services/studio/StudioQuery/BackgroundLabel/BackgroundLabelQuery.services';
import type { AIModelSelectionType, AIGenerationConfig, HangerCard } from '../../context/MannequinContext';

export const MAX_AZURE_CARDS_PER_SESSION = 2;

/** Beden bilgisi accordion + prompt'a beden ekleme. Kullanıma açmak için true yap. */
export const ENABLE_HANGER_CARD_SIZE_INFO = true;

const DEFAULT_NEW_CARD_MODEL: AIModelSelectionType = 'GoogleGemini';

/** YYMMDDHHmmssSS — yıl, ay, gün, saat, dakika, saniye, salise (ör. 26091611230105). */
const createDuplicateBarcodeTimestamp = (): string => {
    const now = new Date();
    const yy = String(now.getFullYear() % 100).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    const cs = String(Math.floor(now.getMilliseconds() / 10)).padStart(2, '0');
    return `${yy}${mm}${dd}${hh}${min}${ss}${cs}`;
};

/**
 * API ve beden bilgisi için 13 haneli ürün barkodunu döner; _suffix temizlenir.
 * Duplicate kart soneki UI'da kalır; bu fonksiyon API çağrılarında kullanılır.
 */
export const normalizeOutputFormatKey = (format?: string): string => {
    const normalized = (format || 'jpeg').toLowerCase();
    return normalized === 'jpg' ? 'jpeg' : normalized;
};

export const getOutputFormatLabel = (format?: string): string =>
    normalizeOutputFormatKey(format) === 'png' ? 'PNG' : 'JPEG';

export const inferOutputFormatFromImageUrl = (url?: string): string | undefined => {
    if (!url) {
        return undefined;
    }

    const mimeMatch = /^data:image\/(jpeg|jpg|png|webp)/i.exec(url);
    if (mimeMatch) {
        const format = mimeMatch[1].toLowerCase();
        return format === 'jpg' ? 'jpeg' : format;
    }

    const extMatch = /\.(jpg|jpeg|png|webp)(?:\?|$)/i.exec(url);
    if (extMatch) {
        const format = extMatch[1].toLowerCase();
        return format === 'jpg' ? 'jpeg' : format;
    }

    return undefined;
};

export const shouldShowNextGenerationFormatHint = (outputFormat: string, generatedOutputFormat?: string): boolean =>
    Boolean(generatedOutputFormat) &&
    normalizeOutputFormatKey(outputFormat) !== normalizeOutputFormatKey(generatedOutputFormat);

export const resolveBaseProductBarcode = (barcode: string): string => {
    const trimmed = barcode.trim();
    if (!trimmed) return '';

    const ean13 = trimmed.match(/(?:^|[^0-9])(8\d{12})(?:[^0-9]|$)/)?.[1] ?? trimmed.match(/^(8\d{12})/)?.[1];
    if (ean13) return ean13;

    const beforeUnderscore = trimmed.split('_')[0];
    return /^\d+$/.test(beforeUnderscore) ? beforeUnderscore : trimmed;
};

/**
 * Extracts barcode from filename and returns it in `BARCODE_TIMESTAMP` format.
 * Removes file extension and any +GUID suffix (e.g. "1234567890+a3f8-..." → "1234567890").
 * Prefer 13-digit barcode starting with 8; all other filename text is discarded.
 * Always appends `_TIMESTAMP` so each uploaded photo gets a unique barcode value.
 * API calls use resolveBaseProductBarcode to strip the suffix and get the raw barcode.
 */
export const extractBarcodeFromFilename = (filename: string): string => {
    if (!filename) return '';

    const lastDotIndex = filename.lastIndexOf('.');
    const nameWithoutExt = lastDotIndex === -1 ? filename : filename.substring(0, lastDotIndex);
    // + sonrasındaki GUID kısmını temizle
    const plusIndex = nameWithoutExt.indexOf('+');
    const rawBarcode = (plusIndex > 0 ? nameWithoutExt.substring(0, plusIndex) : nameWithoutExt).trim();

    // 8 ile başlayan 13 haneli barkodu ayıkla; mevcut _suffix varsa önce kökü al
    const thirteenDigitBarcode = rawBarcode.match(/8\d{12}/)?.[0];
    if (thirteenDigitBarcode) {
        return `${thirteenDigitBarcode}_${createDuplicateBarcodeTimestamp()}`;
    }

    // 13 haneli barkod yoksa: mevcut _suffix varsa kökü al, sonra timestamp ekle
    const underscoreIndex = rawBarcode.indexOf('_');
    const barcodeRoot = underscoreIndex > 0 ? rawBarcode.substring(0, underscoreIndex) : rawBarcode;
    return `${barcodeRoot}_${createDuplicateBarcodeTimestamp()}`;
};

/**
 * Compresses an image file to reduce size for API upload
 * Resizes image if larger than maxWidth/maxHeight
 * Compresses to JPEG format with specified quality
 * @param file - The original image file
 * @param maxWidth - Maximum width (default: 2048)
 * @param maxHeight - Maximum height (default: 2048)
 * @param quality - JPEG compression quality 0-1 (default: 0.85)
 * @returns Promise<string> - Base64 encoded compressed image
 */
export const compressImage = (
    file: File,
    maxWidth: number = 2048,
    maxHeight: number = 2048,
    quality: number = 0.85
): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            const img = new Image();

            img.onload = () => {
                // Calculate new dimensions while maintaining aspect ratio
                let width = img.width;
                let height = img.height;

                if (width > maxWidth || height > maxHeight) {
                    const aspectRatio = width / height;

                    if (width > height) {
                        width = maxWidth;
                        height = width / aspectRatio;
                    } else {
                        height = maxHeight;
                        width = height * aspectRatio;
                    }
                }

                // Create canvas and draw resized image
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Failed to get canvas context'));
                    return;
                }

                // Use better image smoothing
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';

                // Draw image on canvas
                ctx.drawImage(img, 0, 0, width, height);

                // Convert to base64 with compression
                let mimeType = 'image/jpeg';
                if (file.type === 'image/png') mimeType = 'image/png';
                else if (file.type === 'image/webp') mimeType = 'image/webp';

                const compressedDataUrl = canvas.toDataURL(mimeType, mimeType === 'image/png' ? undefined : quality);
                resolve(compressedDataUrl);
            };

            img.onerror = () => {
                reject(new Error('Failed to load image'));
            };

            img.src = e.target?.result as string;
        };

        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };

        reader.readAsDataURL(file);
    });
};

/**
 * Parses imageUrls JSON string from backend to array
 * Handles both JSON string arrays and already-parsed arrays
 * @param imageUrlsJson - JSON string containing array of image URLs, or already-parsed array
 * @returns Array of image URLs or empty array if parsing fails
 */
export const parseImageUrls = (imageUrlsJson: string | string[]): string[] => {
    // Already an array (e.g. after deserialization)
    if (Array.isArray(imageUrlsJson)) {
        return imageUrlsJson.filter((url) => typeof url === 'string' && url.length > 0);
    }

    if (!imageUrlsJson || typeof imageUrlsJson !== 'string') return [];

    try {
        const parsed = JSON.parse(imageUrlsJson);
        if (Array.isArray(parsed)) {
            return parsed.filter((url: any) => typeof url === 'string' && url.length > 0);
        }
        // Single URL string case
        if (typeof parsed === 'string' && parsed.length > 0) return [parsed];
        return [];
    } catch {
        // If it's a plain URL (not JSON), return as single-element array
        if (imageUrlsJson.startsWith('http') || imageUrlsJson.startsWith('data:')) {
            return [imageUrlsJson];
        }
        return [];
    }
};

/**
 * Creates PhotoFile array from image URLs (for history regeneration)
 * @param imageUrls - Array of image URLs
 * @returns Array of PhotoFile objects
 */
export const createPhotoFilesFromHistory = (imageUrls: string[]): any[] => {
    return imageUrls.map((url) => {
        // Extract filename from URL if possible
        const filename = url.split('/').pop() || 'unknown';

        return {
            id: crypto.randomUUID(),
            file: null,
            url: url,
            preview: url,
            type: 'product' as const,
            name: filename,
        };
    });
};

/**
 * Formats date as relative time (e.g., "2 days ago", "3 hours ago")
 * @param dateString - ISO date string
 * @returns Formatted relative time string
 */
export const getRelativeTime = (dateString: string): string => {
    try {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (diffInSeconds < 60) {
            return 'Az önce';
        }

        const diffInMinutes = Math.floor(diffInSeconds / 60);
        if (diffInMinutes < 60) {
            return `${diffInMinutes} dakika önce`;
        }

        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) {
            return `${diffInHours} saat önce`;
        }

        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 7) {
            return `${diffInDays} gün önce`;
        }

        const diffInWeeks = Math.floor(diffInDays / 7);
        if (diffInWeeks < 4) {
            return `${diffInWeeks} hafta önce`;
        }

        const diffInMonths = Math.floor(diffInDays / 30);
        if (diffInMonths < 12) {
            return `${diffInMonths} ay önce`;
        }

        const diffInYears = Math.floor(diffInDays / 365);
        return `${diffInYears} yıl önce`;
    } catch {
        return dateString;
    }
};

/**
 * Fetches an S3 image through the BFF proxy and returns it as a base64 data URL.
 * This avoids CORS issues by routing through the BFF which has direct S3 access.
 * @param s3Key - The S3 key of the image (e.g., "DEV/studio/backgrounds-labels/...")
 * @returns Promise<string> - Base64 encoded data URL (e.g., "data:image/jpeg;base64,...")
 */
export const fetchImageAsBase64 = async (s3Key: string): Promise<string> => {
    const response = await BackgroundLabelQuery.GetImageBase64(s3Key);
    const data = response?.data || response;
    const base64 = data?.base64;

    if (!base64) {
        throw new Error('Failed to get base64 image from BFF');
    }

    return base64;
};

/**
 * Returns the display label for an AI model type.
 * modelType 0 = EachLabs, 1 = Google Gemini, 2 = Azure OpenAI, 3 = Nano Banana 2, 4 = AIGate, 5 = Gemini Pro LCW, 6 = AIGate Pro LCW, 7 = Nano Banana 2.1, 8 = Nano Banana 2.1 LCW
 */
export const getModelTypeLabel = (modelType: number | undefined, short = false): string => {
    const labelKeyByModelType: Record<number, string> = {
        0: 'mannequin.aiGeneration.modelTypes.eachLabs',
        1: short
            ? 'mannequin.aiGeneration.modelTypes.googleGeminiShort'
            : 'mannequin.aiGeneration.modelTypes.googleGemini',
        2: 'mannequin.aiGeneration.modelTypes.azureOpenAI',
        3: 'mannequin.aiGeneration.modelTypes.googleGeminiFlash',
        4: 'mannequin.aiGeneration.modelTypes.aiGate',
        5: 'mannequin.aiGeneration.modelTypes.googleGeminiProLcw',
        6: 'mannequin.aiGeneration.modelTypes.aiGateProLcw',
        7: 'mannequin.aiGeneration.modelTypes.googleGeminiNanoBanana21',
        8: 'mannequin.aiGeneration.modelTypes.googleGeminiNanoBanana21Lcw',
    };

    const key = labelKeyByModelType[modelType ?? 0] ?? labelKeyByModelType[0];
    return i18n.t(key);
};

export const resolveHangerCardModelType = (selectedModel: AIModelSelectionType): number => {
    switch (selectedModel) {
        case 'GoogleGemini':
            return 1;
        case 'AzureOpenAIGpt':
            return 2;
        case 'GoogleGeminiFlash':
            return 3;
        case 'AIGate':
            return 4;
        case 'GeminiProLcw':
            return 5;
        case 'AIGateProLcw':
            return 6;
        case 'GoogleGeminiNanoBanana21':
            return 7;
        case 'GoogleGeminiNanoBanana21Lcw':
            return 8;
        default:
            return 0;
    }
};

export const isGeminiLikeHangerModel = (selectedModel: AIModelSelectionType): boolean =>
    selectedModel === 'GoogleGemini' ||
    selectedModel === 'GoogleGeminiFlash' ||
    selectedModel === 'GoogleGeminiNanoBanana21' ||
    selectedModel === 'GoogleGeminiNanoBanana21Lcw' ||
    selectedModel === 'GeminiProLcw';

export const isGeminiProLcwHangerModel = (selectedModel: AIModelSelectionType): boolean =>
    selectedModel === 'GeminiProLcw';

export const isAIGateProLcwHangerModel = (selectedModel: AIModelSelectionType): boolean =>
    selectedModel === 'AIGateProLcw';

export const isNanoBanana21LcwHangerModel = (selectedModel: AIModelSelectionType): boolean =>
    selectedModel === 'GoogleGeminiNanoBanana21Lcw';

export const isLcwUpscaleHangerModel = (selectedModel: AIModelSelectionType): boolean =>
    isGeminiProLcwHangerModel(selectedModel) ||
    isAIGateProLcwHangerModel(selectedModel) ||
    isNanoBanana21LcwHangerModel(selectedModel);

export const resolveHangerCardResolution = (selectedModel: AIModelSelectionType, resolution: string): string =>
    isLcwUpscaleHangerModel(selectedModel) ? '4K' : resolution;

export const supportsHangerCardQualitySetting = (selectedModel: AIModelSelectionType): boolean =>
    selectedModel === 'AzureOpenAIGpt' || selectedModel === 'AIGate';

export const resolveHangerCardQualityFallback = (selectedModel: AIModelSelectionType): string => {
    if (selectedModel === 'AIGate') {
        return 'medium';
    }

    if (selectedModel === 'AzureOpenAIGpt') {
        return 'high';
    }

    return 'high';
};

/** API isteğinde kullanılacak kalite; AIGate Pro LCW her zaman medium. */
export const resolveHangerCardQualityForRequest = (
    selectedModel: AIModelSelectionType,
    configuredQuality?: string
): string => {
    if (selectedModel === 'AIGateProLcw') {
        return 'medium';
    }

    return configuredQuality || resolveHangerCardQualityFallback(selectedModel);
};

/**
 * Formats date as localized string
 * @param dateString - ISO date string
 * @returns Formatted date string (e.g., "15 Ocak 2024, 14:30")
 */
export const formatDate = (dateString: string): string => {
    try {
        const date = new Date(dateString);
        return date.toLocaleString('tr-TR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return dateString;
    }
};

export type HangerCardModelIntent = 'new' | 'duplicate' | 'change';

export type ResolveHangerCardModelSelectionInput = {
    cards: HangerCard[];
    intent: HangerCardModelIntent;
    requestedModel?: AIModelSelectionType;
    cardId?: string;
    config?: AIGenerationConfig;
};

export type ResolveHangerCardModelSelectionResult = {
    selectedModel: AIModelSelectionType;
    config: AIGenerationConfig;
    azureLimitApplied: boolean;
};

export const createEmptyHangerCardConfig = (): AIGenerationConfig => ({
    barcode: '',
    prefix: '',
    suffix: '',
    prompt: '',
    resolution: '4K',
    aspectRatio: '3:4',
    outputFormat: 'jpeg',
    outputCount: 1,
    selectedSizeCode: '',
    sizeMeasurementText: '',
    sizeInfoSizes: [],
    sizeInfoProductAttributes: [],
    sizeInfoLoaded: false,
});

/**
 * Kopyalanan kart için mevcut barkodlarla çakışmayan `barcode_TIMESTAMP` üretir.
 * Önceki duplicate soneklerini (_1, _TIMESTAMP) temizleyerek kök barkodu korur.
 */
export const resolveDuplicateBarcode = (sourceBarcode: string, existingBarcodes: string[]): string => {
    const trimmed = sourceBarcode.trim();
    if (!trimmed) {
        return trimmed;
    }

    // Eski `_1` / yeni timestamp soneklerini temizle; yeniden kopyalamada `barcode_1_1` oluşmasın
    const baseBarcode = trimmed.replace(/(_\d+)+$/, '') || trimmed;
    const existingSet = new Set(existingBarcodes.map((barcode) => barcode.trim()).filter(Boolean));

    for (let attempt = 0; attempt < 5; attempt += 1) {
        const candidate = `${baseBarcode}_${createDuplicateBarcodeTimestamp()}`;
        if (!existingSet.has(candidate)) {
            return candidate;
        }
    }

    // Son çare: 6 haneli rastgele sonek
    const fallback = String(window.crypto.getRandomValues(new Uint32Array(1))[0] % 1_000_000).padStart(6, '0');
    return `${baseBarcode}_${fallback}`;
};

const countAzureCards = (cards: HangerCard[], excludeCardId?: string): number =>
    cards.filter((card) => card.selectedModel === 'AzureOpenAIGpt' && card.id !== excludeCardId).length;

export const isAzureModelSelectionBlocked = (cards: HangerCard[], excludeCardId?: string): boolean =>
    countAzureCards(cards, excludeCardId) >= MAX_AZURE_CARDS_PER_SESSION;

const getAzureConfigDefaults = (): Pick<AIGenerationConfig, 'resolution' | 'aspectRatio' | 'quality'> => ({
    resolution: '2160x3840',
    aspectRatio: '9:16',
    quality: 'high',
});

const getAIGateConfigDefaults = (): Pick<AIGenerationConfig, 'resolution' | 'aspectRatio' | 'quality'> => ({
    resolution: '2160x3840',
    aspectRatio: '9:16',
    quality: 'medium',
});

const getAIGateProLcwConfigDefaults = (): Pick<AIGenerationConfig, 'resolution' | 'quality'> => ({
    resolution: '4K',
    quality: 'medium',
});

const applyModelConfigDefaults = (model: AIModelSelectionType, config: AIGenerationConfig): AIGenerationConfig => {
    if (model === 'AzureOpenAIGpt') {
        return { ...config, ...getAzureConfigDefaults() };
    }

    if (model === 'AIGate') {
        return { ...config, ...getAIGateConfigDefaults() };
    }

    if (model === 'GeminiProLcw' || model === 'GoogleGeminiNanoBanana21Lcw') {
        return { ...config, resolution: '4K' };
    }

    if (model === 'AIGateProLcw') {
        return { ...config, ...getAIGateProLcwConfigDefaults() };
    }

    return { ...config, resolution: '4K' };
};

/**
 * Azure en fazla 2 kartta kullanılabilir.
 * Limit doluysa yeni/kopyalanan/manuel seçimlerde AIGate döner.
 */
export const resolveHangerCardModelSelection = (
    input: ResolveHangerCardModelSelectionInput
): ResolveHangerCardModelSelectionResult => {
    const { cards, intent, requestedModel, cardId, config = createEmptyHangerCardConfig() } = input;

    let selectedModel: AIModelSelectionType;

    if (intent === 'new') {
        selectedModel = isAzureModelSelectionBlocked(cards) ? 'AIGate' : DEFAULT_NEW_CARD_MODEL;
    } else {
        const model = requestedModel ?? DEFAULT_NEW_CARD_MODEL;
        selectedModel = model === 'AzureOpenAIGpt' && isAzureModelSelectionBlocked(cards, cardId) ? 'AIGate' : model;
    }

    const azureLimitApplied =
        (intent === 'new' && selectedModel === 'AIGate' && isAzureModelSelectionBlocked(cards)) ||
        (requestedModel === 'AzureOpenAIGpt' && selectedModel === 'AIGate');

    return {
        selectedModel,
        config: intent === 'duplicate' ? config : applyModelConfigDefaults(selectedModel, config),
        azureLimitApplied,
    };
};

/** Mevcut kart listesinde 3. ve sonraki Azure seçimlerini AIGate'e çevirir. */
export const normalizeHangerCardsModelPolicy = (cards: HangerCard[]): HangerCard[] => {
    let azureCount = 0;

    return cards.map((card) => {
        if (card.selectedModel !== 'AzureOpenAIGpt') {
            return card;
        }

        azureCount += 1;
        if (azureCount <= MAX_AZURE_CARDS_PER_SESSION) {
            return card;
        }

        const { selectedModel, config } = resolveHangerCardModelSelection({
            cards,
            intent: 'change',
            requestedModel: 'AIGate',
            cardId: card.id,
            config: card.config,
        });

        return {
            ...card,
            selectedModel,
            config,
        };
    });
};

export const hasHangerCardModelPolicyChanges = (before: HangerCard[], after: HangerCard[]): boolean =>
    before.some((card, index) => card.selectedModel !== after[index]?.selectedModel);
